import type { ECharts } from "echarts/core";
import { type JSX, useCallback } from "react";
import { layoutTextMeasurer } from "@/utils/text";
import { formatReadingValue } from "../../format";
import {
    HOVER_LABEL_BORDER_PX,
    HOVER_LABEL_FONT,
    HOVER_LABEL_FONT_SIZE_PX,
    HOVER_LABEL_FONT_WEIGHT,
    HOVER_LABEL_PADDING_X_PX,
    HOVER_LABEL_RADIUS_PX,
    HOVER_LABEL_TEXT_COLOR,
} from "../layout/plot-geometry";
import { dotTimeMs, dottedRows, type Readout } from "../readout/readout";
import { usePanelReadout } from "../readout/readout-context";
import { useChartProjection } from "../use-chart-projection";
import { TAG_MIN_GAP_PX } from "./hover-tags-fit";
import { stackTagOffsets } from "./stack-tags";

/** Tags are read in a stack of twenty, so they carry their series colour as a tint, not a fill. */
const TAG_FILL_PERCENT = 16;
const TAG_BORDER_PERCENT = 38;
/** Gap between a dot and the tag beside it. */
const TAG_GAP_PX = 8;
const TAG_BOX_EXTRA_PX = 2 * (HOVER_LABEL_PADDING_X_PX + HOVER_LABEL_BORDER_PX);

type Side = "left" | "right";

/** A series' dot at the readout's moment, plus the value tag beside it when tags are on. */
type Mark = {
    streamId: number;
    color: string;
    text: string;
    dotX: number;
    dotY: number;
    side: Side;
    tagY: number | null;
};

type PlotBox = {
    topPx: number;
    bottomPx: number;
    widthPx: number;
    hoverTags: boolean;
    focusedStreamId: number | undefined;
};

function tagWidth(text: string): number {
    return layoutTextMeasurer(text, HOVER_LABEL_FONT) + TAG_BOX_EXTRA_PX;
}

/**
 * Alternating sides down the panel gives the tags two columns to spread into, so series whose
 * values sit close together stop competing for one strip. A tag with no room on its side takes
 * the other.
 */
function sideFor(dotX: number, text: string, index: number, panelWidthPx: number): Side {
    const width = tagWidth(text);
    const fitsLeft = dotX - TAG_GAP_PX - width >= 0;
    const fitsRight = dotX + TAG_GAP_PX + width <= panelWidthPx;

    if (index % 2 === 0) return fitsLeft || !fitsRight ? "left" : "right";
    return fitsRight || !fitsLeft ? "right" : "left";
}

function buildMarks(chart: ECharts, readout: Readout, plot: PlotBox): Mark[] {
    const candidates = dottedRows(readout, plot.focusedStreamId).flatMap((row) => {
        const entry = row.series;
        if (row.value === null) return [];
        const timeMs = dotTimeMs(readout, row);
        if (timeMs === null) return [];

        // By id, matching how buildTimePanelOptions binds the series. Addressing it by index would
        // hold only while the yAxis array happens to be in spec.axes order.
        const pixel = chart.convertToPixel({ xAxisIndex: 0, yAxisId: entry.axisId }, [timeMs, row.value]);
        if (!Array.isArray(pixel) || !Number.isFinite(pixel[0]) || !Number.isFinite(pixel[1])) return [];

        // A snapped reading can sit outside the window when zoomed in.
        if (!chart.containPixel({ gridIndex: 0 }, pixel as [number, number])) return [];

        return [
            {
                streamId: entry.streamId,
                color: entry.color,
                text: formatReadingValue(row.value),
                // Subpixel values make this look like an oval rather than a circle
                dotX: Math.round(pixel[0]),
                dotY: Math.round(pixel[1]),
            },
        ];
    });

    const sided = [...candidates]
        .sort((a, b) => a.dotY - b.dotY)
        .map((mark, index) => ({ ...mark, side: sideFor(mark.dotX, mark.text, index, plot.widthPx) }));

    if (!plot.hoverTags) return sided.map((mark) => ({ ...mark, tagY: null }));

    const columns = (["left", "right"] as const).map((side) =>
        stackTagOffsets(
            sided.filter((mark) => mark.side === side).map((mark) => ({ item: mark, y: mark.dotY })),
            { minGapPx: TAG_MIN_GAP_PX, topPx: plot.topPx, bottomPx: plot.bottomPx },
        ),
    );

    // All or nothing: a column the plot can't separate would leave half the series tagged.
    // hoverTagsFit sizes for the worst case, every tag in one column, so this should not happen.
    if (columns.some((column) => column === null)) return sided.map((mark) => ({ ...mark, tagY: null }));

    return columns.flatMap((column) => (column ?? []).map(({ item, y }) => ({ ...item, tagY: y })));
}

// The whole list, so a field added to Mark can't be left out of the check.
const sameMarks = (previous: Mark[], next: Mark[]) => JSON.stringify(previous) === JSON.stringify(next);

/** A dot per series at the readout's moment, with a value tag beside each while hovering. */
export function HoverValues({
    chart,
    plotTopPx,
    plotBottomPx,
    panelWidthPx,
    hoverTags,
    focusedStreamId,
}: {
    chart: ECharts | null;
    plotTopPx: number;
    plotBottomPx: number;
    panelWidthPx: number;
    hoverTags: boolean;
    focusedStreamId?: number;
}): JSX.Element | null {
    const { readout } = usePanelReadout();
    const tagsOn = hoverTags && readout.mode === "cursor";

    const project = useCallback(
        (instance: ECharts) =>
            buildMarks(instance, readout, {
                topPx: plotTopPx,
                bottomPx: plotBottomPx,
                widthPx: panelWidthPx,
                hoverTags: tagsOn,
                focusedStreamId,
            }),
        [readout, plotTopPx, plotBottomPx, panelWidthPx, tagsOn, focusedStreamId],
    );
    const marks = useChartProjection(chart, project, sameMarks);
    if (!marks) return null;

    return (
        <div className={"pointer-events-none absolute inset-0"}>
            {marks.map((mark) => {
                const onRight = mark.side === "right";

                return (
                    <div key={mark.streamId}>
                        <div
                            className={"absolute z-1 size-[6px] -translate-1/2 rounded-full ring-2 ring-background"}
                            style={{ left: mark.dotX, top: mark.dotY, backgroundColor: mark.color }}
                        />
                        {mark.tagY !== null && (
                            <div
                                className={"absolute z-2 flex items-center leading-none whitespace-nowrap tabular-nums"}
                                style={{
                                    left: mark.dotX + (onRight ? TAG_GAP_PX : -TAG_GAP_PX),
                                    top: mark.tagY,
                                    transform: onRight ? "translateY(-50%)" : "translate(-100%, -50%)",
                                    // Height, not padding: TAG_MIN_GAP_PX is what hoverTagsFit reserved, so a taller tag would overlap.
                                    height: TAG_MIN_GAP_PX,
                                    paddingInline: HOVER_LABEL_PADDING_X_PX,
                                    fontSize: HOVER_LABEL_FONT_SIZE_PX,
                                    fontWeight: HOVER_LABEL_FONT_WEIGHT,
                                    borderRadius: HOVER_LABEL_RADIUS_PX,
                                    backgroundColor: `color-mix(in srgb, ${mark.color} ${TAG_FILL_PERCENT}%, white)`,
                                    border: `${HOVER_LABEL_BORDER_PX}px solid color-mix(in srgb, ${mark.color} ${TAG_BORDER_PERCENT}%, white)`,
                                    color: HOVER_LABEL_TEXT_COLOR,
                                }}
                            >
                                {mark.text}
                            </div>
                        )}
                    </div>
                );
            })}
        </div>
    );
}
